import { useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../../theme/colors';

interface BulkOperationProgressProps {
  isOperating: boolean;
  operationName?: string;
  totalItems?: number;
  successCount?: number;
  failureCount?: number;
}

export const BulkOperationProgress = ({
  isOperating,
  operationName = 'Processing',
  totalItems,
  successCount = 0,
  failureCount = 0,
}: BulkOperationProgressProps) => {
  const { mode } = useContext(ColorModeContext);

  const colors = mode === 'dark' ? {
    bg: palette.surface,
    bgElevated: palette.surfaceElevated,
    border: palette.border,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.surface,
    bgElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  if (!isOperating && successCount === 0 && failureCount === 0) {
    return null;
  }

  const processedCount = successCount + failureCount;
  const progress = totalItems && totalItems > 0
    ? (processedCount / totalItems) * 100
    : undefined;
  const isComplete = totalItems !== undefined && processedCount >= totalItems;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: themeColors.radius.xl,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bgElevated,
      }}
    >
      {/* Progress Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: colors.text,
            fontWeight: typography.fontWeight.semibold,
            mb: 1,
          }}
        >
          {isOperating && !isComplete ? `${operationName}...` : 'Operation Complete'}
        </Typography>

        {/* Progress Bar */}
        {isOperating && !isComplete && (
          <LinearProgress
            variant={progress !== undefined ? 'determinate' : 'indeterminate'}
            value={progress}
            sx={{
              height: 6,
              borderRadius: themeColors.radius.sm,
              backgroundColor: colors.border,
              '& .MuiLinearProgress-bar': {
                backgroundColor: palette.primary,
                borderRadius: themeColors.radius.sm,
              },
            }}
          />
        )}
      </Box>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        {/* Success Count */}
        {successCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 20,
                color: palette.success,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: palette.success,
                  fontWeight: typography.fontWeight.bold,
                  mr: 0.5,
                }}
              >
                {successCount}
              </Box>
              {successCount === 1 ? 'success' : 'successful'}
            </Typography>
          </Box>
        )}

        {/* Failure Count */}
        {failureCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon
              sx={{
                fontSize: 20,
                color: palette.error,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: palette.error,
                  fontWeight: typography.fontWeight.bold,
                  mr: 0.5,
                }}
              >
                {failureCount}
              </Box>
              {failureCount === 1 ? 'failure' : 'failed'}
            </Typography>
          </Box>
        )}

        {/* Total Progress (optional) */}
        {totalItems !== undefined && totalItems > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
            }}
          >
            {processedCount} / {totalItems}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
